import { useTheme } from "@material-ui/core";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useAutoFit } from "../hooks/useAutoFit";
import { useCanvasPanning } from "../hooks/useCanvasPanning";
import { useCanvasResize } from "../hooks/useCanvasResize";
import { useCanvasTouchGestures } from "../hooks/useCanvasTouchGestures";
import { useFindBoundaryPoint } from "../hooks/useFindBoundaryPoint";
import { usePointDragging } from "../hooks/usePointDragging";
import { useRotation } from "../hooks/useRotation";
import { useViewTransform } from "../hooks/useViewTransform";
import type { BoundaryDraggingPoint, ReadyConfig, ViewState } from "../shared/types";
import { ControlPanel } from "./ControlPanel";
import { EditorCanvasRenderer } from "./EditorCanvasRenderer";
import { GPSEditorInstructions } from "./GPSEditorInstructions";
import { BottomPanelContainer } from "./BottomPanelContainer";
import { HelpButton } from "./HelpButton";

interface GPSEditorProps {
  readyConfig: ReadyConfig;
  onConfigUpdate: (config: ReadyConfig) => void;
  viewState: ViewState;
  setViewState: React.Dispatch<React.SetStateAction<ViewState>>;
  showAreasPoints: boolean;
  showRowsPoints: boolean;
  showPortsPoints: boolean;
  selectedRowIndex?: number | null;
  onSelectedRowChange?: (rowIndex: number | null) => void;
}

export const GPSEditor: React.FC<GPSEditorProps> = ({
  readyConfig,
  onConfigUpdate,
  viewState,
  setViewState,
  showAreasPoints,
  showRowsPoints,
  showPortsPoints,
  selectedRowIndex: externalSelectedRowIndex,
  onSelectedRowChange,
}) => {
    const theme = useTheme();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [tempConfig, setTempConfig] = useState<ReadyConfig | null>(null);

    // Use external selected row index if provided, otherwise use internal state
    const [internalSelectedRowIndex, setInternalSelectedRowIndex] = useState<number | null>(null);
    const selectedRowIndex = externalSelectedRowIndex !== undefined ? externalSelectedRowIndex : internalSelectedRowIndex;
    const setSelectedRowIndex = onSelectedRowChange || setInternalSelectedRowIndex;

    const { areaBoundaries, rowBoundaries, portBoundaries } = readyConfig;

    // Canvas resize hook
    const { canvasSize } = useCanvasResize({ containerRef });

    const { worldToScreen, screenToWorld, zoomIn, zoomOut } = useViewTransform({
      viewState,
      setViewState,
      canvasSize,
      canvasRef,
    });

    const { autoFit } = useAutoFit({
      boundaries: areaBoundaries,
      canvasSize,
      setViewState,
      autoRun: false,
    });

    // Auto-fit only on mount
    useEffect(() => {
      autoFit();
    }, []);

    const {
      handleRotateLeftMouseDown,
      handleRotateRightMouseDown,
      handleRotateMouseUp,
      resetRotation,
    } = useRotation({
      setViewState,
    });

    const { findBoundaryPoint } = useFindBoundaryPoint({
      areaBoundaries,
      rowBoundaries,
      portBoundaries,
      worldToScreen,
      showAreas: showAreasPoints,
      showRows: showRowsPoints,
      showPorts: showPortsPoints,
    });

    // Check if point is inside polygon using ray casting algorithm
    const isPointInPolygon = useCallback(
      (point: { x: number; y: number }, polygon: Array<{ x: number; y: number }>) => {
        let inside = false;
        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
          const xi = polygon[i].x;
          const yi = polygon[i].y;
          const xj = polygon[j].x;
          const yj = polygon[j].y;

          const intersect = yi > point.y !== yj > point.y &&
            point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
          if (intersect) inside = !inside;
        }
        return inside;
      },
      []
    );

    // Find which row was clicked
    const findClickedRow = useCallback(
      (screenX: number, screenY: number): number | null => {
        const worldPoint = screenToWorld(screenX, screenY);

        // Check rows in reverse order (last drawn = on top)
        for (let i = rowBoundaries.length - 1; i >= 0; i--) {
          if (isPointInPolygon(worldPoint, rowBoundaries[i].polygon)) {
            return i;
          }
        }
        return null;
      },
      [rowBoundaries, screenToWorld, isPointInPolygon]
    );

    const updateCoordinate = useCallback(
      (point: BoundaryDraggingPoint, newCoord: { x: number; y: number }) => {
        const newConfig = JSON.parse(JSON.stringify(readyConfig)) as ReadyConfig;

        if (point.type === "port") {
          newConfig.portBoundaries[point.boundaryIdx].polygon[point.pointIdx] = newCoord;
        } else if (point.type === "row") {
          newConfig.rowBoundaries[point.boundaryIdx].polygon[point.pointIdx] = newCoord;
        } else if (point.type === "area") {
          newConfig.areaBoundaries[point.boundaryIdx].polygon[point.pointIdx] = newCoord;

          const polygon = newConfig.areaBoundaries[point.boundaryIdx].polygon;
          const minX = Math.min(...polygon.map((p) => p.x));
          const maxX = Math.max(...polygon.map((p) => p.x));
          const minY = Math.min(...polygon.map((p) => p.y));
          const maxY = Math.max(...polygon.map((p) => p.y));
          newConfig.areaBoundaries[point.boundaryIdx].bounds = { minX, minY, maxX, maxY };
        }

        return newConfig;
      },
      [readyConfig]
    );

    // Panning hook
    const panning = useCanvasPanning({
      canvasRef,
      viewState,
      setViewState,
    });

    // Touch gestures hook for mobile support
    const touchGestures = useCanvasTouchGestures({
      canvasRef,
      setViewState
    });

    // Point dragging hook
    const pointDragging = usePointDragging<BoundaryDraggingPoint>({
      findPoint: (screenX: number, screenY: number) => findBoundaryPoint(screenX, screenY),
      onPointMove: (point: BoundaryDraggingPoint, screenX: number, screenY: number) => {
        const newCoord = screenToWorld(screenX, screenY);
        const newConfig = updateCoordinate(point, newCoord);
        setTempConfig(newConfig);
      },
      onDragEnd: () => {
        if (tempConfig) {
          onConfigUpdate(tempConfig);
        }
        setTempConfig(null);
      },
    });

    // Mouse event handlers - combine panning with point dragging logic
    const handleMouseDown = useCallback(
      (e: React.MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Try to start point dragging first (only for left button)
        if (e.button === 0 && pointDragging.handlePointMouseDown(x, y)) {
          return; // Point found, don't pan
        }

        // Check if clicking on a row to select it (Ctrl + Click)
        if (e.button === 0 && e.ctrlKey) {
          const clickedRowIndex = findClickedRow(x, y);
          setSelectedRowIndex(clickedRowIndex);
          return;
        }

        // No point found or other button - delegate to panning
        panning.handleMouseDown(e);
      },
      [canvasRef, pointDragging, panning, findClickedRow]
    );

    const handleMouseMove = useCallback(
      (e: React.MouseEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Handle point dragging
        pointDragging.handlePointMouseMove(x, y);

        // If not dragging point, handle panning
        if (!pointDragging.draggingPoint) {
          panning.handleMouseMove(e);
        }
      },
      [canvasRef, pointDragging, panning]
    );

    const handleMouseUp = useCallback(() => {
      pointDragging.handlePointMouseUp();
      panning.handleMouseUp();
    }, [pointDragging, panning]);

    const handleMouseLeave = useCallback(() => {
      pointDragging.handlePointMouseLeave();
      panning.handleMouseLeave();
    }, [pointDragging, panning]);

    // Zoom and wheel handled by useViewTransform hook
    // zoomIn, zoomOut are provided by the hook
    // Wheel event is automatically handled in the hook

    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          background: theme.palette.storageUi.backgroundPrimary,
        }}
      >
        <div ref={containerRef} style={{ flex: 1, position: "relative", overflow: "hidden" }}>
          <canvas
            ref={canvasRef}
            width={canvasSize.width}
            height={canvasSize.height}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={touchGestures.handleTouchStart}
            onTouchMove={touchGestures.handleTouchMove}
            onTouchEnd={touchGestures.handleTouchEnd}
            onTouchCancel={touchGestures.handleTouchCancel}
            onContextMenu={(e) => e.preventDefault()}
            style={{
              cursor: (panning.isDragging || touchGestures.isTouching || pointDragging.draggingPoint)
                ? "grabbing"
                : "crosshair",
              background: theme.palette.storageUi.backgroundPrimary,
              display: "block",
              touchAction: "none",
            }}
          />

          <EditorCanvasRenderer
            canvasRef={canvasRef}
            canvasSize={canvasSize}
            areaBoundaries={(tempConfig || readyConfig).areaBoundaries}
            rowBoundaries={(tempConfig || readyConfig).rowBoundaries}
            portBoundaries={(tempConfig || readyConfig).portBoundaries}
            viewState={viewState}
            worldToScreen={worldToScreen}
            showAreasPoints={showAreasPoints}
            showRowsPoints={showRowsPoints}
            showPortsPoints={showPortsPoints}
            selectedRowIndex={selectedRowIndex}
          />

          <BottomPanelContainer>
            <div style={{ pointerEvents: 'auto' }}>
              <HelpButton>
                <GPSEditorInstructions />
              </HelpButton>
            </div>
            <div style={{ pointerEvents: 'auto' }}>
              <ControlPanel
                onZoomIn={zoomIn}
                onZoomOut={zoomOut}
                onAutoFit={autoFit}
                onRotateLeftMouseDown={handleRotateLeftMouseDown}
                onRotateRightMouseDown={handleRotateRightMouseDown}
                onRotateMouseUp={handleRotateMouseUp}
                onResetRotation={resetRotation}
              />
            </div>
          </BottomPanelContainer>
        </div>
      </div>
    );
};
